import { all, put, select, takeEvery } from 'redux-saga/effects';
import { ActionType } from 'typesafe-actions';

import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  fetchSmevMonitoringCharts,
  fetchSmevMonitoringDownloadMessage,
  fetchSmevMonitoringEndpoints,
  fetchSmevMonitoringFindIndex,
  fetchSmevMonitoringMessageById,
  fetchSmevMonitoringMessageTypes,
  fetchSmevMonitoringProcessingStatuses,
  fetchSmevMonitoringSenders,
  fetchSmevMonitoringSendingStatuses,
  fetchSmevMonitoringServiceTypes,
  fetchSmevMonitoringSmevMessageTypes,
  fetchSmevMonitoringSystemTypes,
} from 'APIPackage/SmevMonitoringModule/services/smevMonitoringApi';
import { ISmevMonitoringIndexPage } from 'APIPackage/SmevMonitoringModule/interfaces/ISmevMonitoringIndexPage';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { SERVER_ERROR } from 'CorePackage/MainModule/constants/httpErrors';
import { ISmevMonitoringChart } from 'APIPackage/SmevMonitoringModule/interfaces/ISmevMonitoringChart';
import { ISmevMonitoringIndex } from 'APIPackage/SmevMonitoringModule/interfaces/ISmevMonitoringIndex';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import {
  smevMonitoringSetSystemTypes,
  smevMonitoringSetTableData,
  smevMonitoringSetMessageTypes,
  smevMonitoringGetSystemTypes,
  smevMonitoringGetMessageTypes,
  smevMonitoringGetServiceTypes,
  smevMonitoringSetServiceTypes,
  smevMonitoringGetEndpoints,
  smevMonitoringSetEndpoints,
  smevMonitoringGetCharts,
  smevMonitoringSetCharts,
  smevMonitoringGetMessageById,
  smevMonitoringSetMessageById,
  smevMonitoringDownloadMessage,
  smevMonitoringGetChartsWithEndpoints,
  smevMonitoringSetProcessingStatuses,
  smevMonitoringSetSendingStatuses,
  smevMonitoringSetAdditionalCharts,
  smevMonitoringResetAdditionalCharts,
  smevMonitoringSetLoadingAdditionalCharts,
  smevMonitoringGetProcessingStatuses,
  smevMonitoringGetSendingStatuses,
  smevMonitoringGetSenders,
  smevMonitoringSetSenders,
  smevMonitoringSetSmevMessageTypes,
  smevMonitoringGetSmevMessageTypes,
} from '../actions/smevMonitoringActions';
import { SmevMonitoringActionsEnum } from '../enums/SmevMonitoringActionsEnum';
import {
  getSmevMonitoringEndpoints,
  getSmevMonitoringFilter,
  getSmevMonitoringOrder,
  getSmevMonitoringPage,
  getSmevMonitoringServiceTypesBySystemType,
  getSmevMonitoringSystemTypes,
  getSmevMonitoringTableData,
} from '../selectors/smevMonitoringSelectors';
import { mapDataToSmevMonitoringRow } from '../common/mapDataToSmevMonitoringRow';

const PAGE_SIZE = 50;

export function* watchSmevMonitoringSaga() {
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_INIT_PAGE, smevMonitoringInitPageSaga);
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_GET_TABLE_DATA, smevMonitoringGetTableDataSaga);
  yield takeEvery(
    SmevMonitoringActionsEnum.SMEV_MONITORING_GET_TABLE_DATA_NEXT_PAGE,
    smevMonitoringGetTableDataNextPageSaga,
  );
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_GET_SYSTEM_TYPES, smevMonitoringGetSystemTypesSaga);
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_GET_MESSAGE_TYPES, smevMonitoringGetMessageTypesSaga);
  yield takeEvery(
    SmevMonitoringActionsEnum.SMEV_MONITORING_GET_SMEV_MESSAGE_TYPES,
    smevMonitoringGetSmevMessageTypesSaga,
  );
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_GET_SERVICE_TYPES, smevMonitoringGetServiceTypesSaga);
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_GET_ENDPOINTS, smevMonitoringGetEndpointsSaga);
  yield takeEvery(
    SmevMonitoringActionsEnum.SMEV_MONITORING_GET_PROCESSING_STATUSES,
    smevMonitoringGetProcessingStatusesSaga,
  );
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_GET_SENDING_STATUSES, smevMonitoringGetSendingStatusesSaga);
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_GET_CHARTS, smevMonitoringGetChartsSaga);
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_GET_MESSAGE_BY_ID, smevMonitoringGetMessageByIdSaga);
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_DOWNLOAD_MESSAGE, smevMonitoringDownloadMessageSaga);
  yield takeEvery(
    SmevMonitoringActionsEnum.SMEV_MONITORING_GET_CHARTS_WITH_ENDPOINTS,
    smevMonitoringGetChartsWithEndpointsSaga,
  );
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_GET_SENDERS, smevMonitoringGetSendersSaga);
  yield takeEvery(SmevMonitoringActionsEnum.SMEV_MONITORING_GET_SYSTEM_CHARTS, smevMonitoringGetSystemChartsSaga);
}

function* smevMonitoringInitPageSaga() {
  yield put(smevMonitoringGetSystemTypes());
  yield put(smevMonitoringGetMessageTypes());
  yield put(smevMonitoringGetSmevMessageTypes());
  yield put(smevMonitoringGetEndpoints());
  yield put(smevMonitoringGetProcessingStatuses());
  yield put(smevMonitoringGetSendingStatuses());
  yield put(smevMonitoringGetSenders());
}

function* serverErrorMessage(err: IResponseError) {
  if (err.message?.includes('Сообщение не найдено')) {
    yield put(
      addNoticeAction({
        renderContent: 'Сообщение не найдено',
        messageType: MessageTypesEnum.SMEV_MONITORING,
        noticeType: NoticeTypesEnum.ERROR,
      }),
    );
  } else {
    yield put(
      addNoticeAction({
        renderContent: SERVER_ERROR,
        messageType: MessageTypesEnum.SMEV_MONITORING,
        noticeType: NoticeTypesEnum.ERROR,
      }),
    );
  }
}

function* smevMonitoringGetTableDataSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const order = getSmevMonitoringOrder(state);
    const filter = getSmevMonitoringFilter(state);
    const res: ISmevMonitoringIndexPage = yield fetchSmevMonitoringFindIndex(0, PAGE_SIZE, filter, order ?? '');

    yield put(
      smevMonitoringSetTableData(
        mapDataToSmevMonitoringRow(res.content),
        res.number,
        res.totalPages,
        res.totalElements,
      ),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield serverErrorMessage(error);
    },
  });
}

function* smevMonitoringGetTableDataNextPageSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const data = getSmevMonitoringTableData(state);
    const page = getSmevMonitoringPage(state);
    const order = getSmevMonitoringOrder(state);
    const filter = getSmevMonitoringFilter(state);
    const res: ISmevMonitoringIndexPage = yield fetchSmevMonitoringFindIndex(page + 1, PAGE_SIZE, filter, order ?? '');

    yield put(
      smevMonitoringSetTableData(
        [...data, ...mapDataToSmevMonitoringRow(res.content)],
        res.number,
        res.totalPages,
        res.totalElements,
      ),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield serverErrorMessage(error);
    },
  });
}

function* smevMonitoringGetSystemTypesSaga() {
  function* funcTry() {
    const systemTypes: string[] = yield fetchSmevMonitoringSystemTypes();

    yield put(smevMonitoringSetSystemTypes(systemTypes)); // eslint-disable-next-line no-restricted-syntax
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield serverErrorMessage(error);
    },
  });
}

function* smevMonitoringGetSystemChartsSaga() {
  try {
    const state: IRootState = yield select();
    const systemTypes = getSmevMonitoringSystemTypes(state);

    yield all(systemTypes.map(item => put(smevMonitoringGetServiceTypes(item.id))));
  } catch (error) {
    yield serverErrorMessage(error as IResponseError);
  }
}

function* smevMonitoringGetMessageTypesSaga() {
  function* funcTry() {
    const messageTypes: string[] = yield fetchSmevMonitoringMessageTypes();

    yield put(smevMonitoringSetMessageTypes(messageTypes));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield serverErrorMessage(error);
    },
  });
}

function* smevMonitoringGetSmevMessageTypesSaga() {
  function* funcTry() {
    const smevMessageTypes: string[] = yield fetchSmevMonitoringSmevMessageTypes();

    yield put(smevMonitoringSetSmevMessageTypes(smevMessageTypes));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield serverErrorMessage(error);
    },
  });
}

function* smevMonitoringGetServiceTypesSaga(action: ActionType<typeof smevMonitoringGetServiceTypes>) {
  try {
    const state: IRootState = yield select();
    const { systemType } = action.payload;
    const isReceived = getSmevMonitoringServiceTypesBySystemType(systemType)(state);

    if (isReceived) {
      return;
    }
    const serviceTypes: string[] = yield fetchSmevMonitoringServiceTypes(systemType);

    yield all(serviceTypes.map(item => item && put(smevMonitoringSetCharts([], true, systemType, item))));
    yield all(serviceTypes.map(item => item && put(smevMonitoringGetCharts(systemType, item))));
    yield put(smevMonitoringSetServiceTypes(systemType, serviceTypes));
  } catch (error) {
    yield serverErrorMessage(error as IResponseError);
  }
}

function* smevMonitoringGetEndpointsSaga() {
  function* funcTry() {
    const endpoints: string[] = yield fetchSmevMonitoringEndpoints();

    yield put(smevMonitoringSetEndpoints(endpoints));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield serverErrorMessage(error);
    },
  });
}

function* smevMonitoringGetProcessingStatusesSaga() {
  function* funcTry() {
    const processingStatuses: string[] = yield fetchSmevMonitoringProcessingStatuses();

    yield put(smevMonitoringSetProcessingStatuses(processingStatuses));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield serverErrorMessage(error);
    },
  });
}

function* smevMonitoringGetSendingStatusesSaga() {
  function* funcTry() {
    const sendingStatuses: string[] = yield fetchSmevMonitoringSendingStatuses();

    yield put(smevMonitoringSetSendingStatuses(sendingStatuses));
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* smevMonitoringGetChartsWithEndpointsSaga(action: ActionType<typeof smevMonitoringGetChartsWithEndpoints>) {
  function* funcTry() {
    const { systemType, serviceType } = action.payload;
    const state: IRootState = yield select();
    const endpoints = getSmevMonitoringEndpoints(state);

    yield put(smevMonitoringSetLoadingAdditionalCharts(true));
    yield put(smevMonitoringResetAdditionalCharts());
    yield all(
      endpoints.map(
        item => item && put(smevMonitoringSetAdditionalCharts([], true, systemType, serviceType, item.value, true)),
      ),
    );
    const charts: ISmevMonitoringChart[][] = yield all(
      endpoints.map(item => item && fetchSmevMonitoringCharts(systemType, serviceType, item.value)),
    );

    yield all(
      endpoints.map(
        (item, index) =>
          item &&
          put(smevMonitoringSetAdditionalCharts(charts[index], false, systemType, serviceType, item.value, true)),
      ),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield serverErrorMessage(error);
    },
    *funcFinally() {
      yield put(smevMonitoringSetLoadingAdditionalCharts(false));
    },
  });
}

function* smevMonitoringGetChartsSaga(action: ActionType<typeof smevMonitoringGetCharts>) {
  try {
    const { systemType, serviceType, endpoint } = action.payload;
    const charts: ISmevMonitoringChart[] = yield fetchSmevMonitoringCharts(systemType, serviceType, endpoint);

    yield put(smevMonitoringSetCharts(charts, false, systemType, serviceType, endpoint));
  } catch (error) {
    yield serverErrorMessage(error as IResponseError);
  }
}

function* smevMonitoringGetMessageByIdSaga(action: ActionType<typeof smevMonitoringGetMessageById>) {
  function* funcTry() {
    const { id } = action.payload;

    const data: ISmevMonitoringIndex = yield fetchSmevMonitoringMessageById(id);

    yield put(smevMonitoringSetMessageById(data));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield serverErrorMessage(error);
    },
  });
}

function* smevMonitoringDownloadMessageSaga(action: ActionType<typeof smevMonitoringDownloadMessage>) {
  function* funcTry() {
    const { id, column } = action.payload;
    const response: Blob = yield fetchSmevMonitoringDownloadMessage(id, column);
    const file = new Blob([response]);

    saveFile(file, `${id}.xml`);
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield serverErrorMessage(error);
    },
  });
}

function* smevMonitoringGetSendersSaga() {
  function* funcTry() {
    const response: string[] = yield fetchSmevMonitoringSenders();

    yield put(smevMonitoringSetSenders(response));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield serverErrorMessage(error);
    },
  });
}
